import { linkOptions, useNavigate, useParams, useSearch } from '@tanstack/react-router';

import { Role } from '../../../../types/Role';
import { SupplierCompanyMessage } from '../../../../types/SupplierCompanyMessage';
import { BreadcrumbLink, Breadcrumbs } from '../../../cdl/Breadcrumbs/Breadcrumbs';
import { Tabs } from '../../../cdl/Tabs/Tabs';
import { Text } from '../../../cdl/Text/Text';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { translate } from '../../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../../common/hooks/useDocumentTitle';
import { useRole } from '../../../common/hooks/useRole';
import { useSupplierDetails } from '../../../common/hooks/useSupplierDetails';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { Page } from '../../../common/Page/Page';
import { Box } from '../../../common/ui/Box';
import { CompanyTabs } from '../../common/CompanyTabs';

import { SupplierDetailsPageTabContent } from './SupplierDetailsPageTabContent';
import { useSupplierDetailsEdit } from './useSupplierDetailsEdit';

const getBreadcrumbLinks = (groupId: string, role: Role): BreadcrumbLink[] => {
    const settings = { label: translate('global.settings'), linkOptions: linkOptions({ to: '/settings' }) };
    const groupManagement = {
        label: translate('settings.groupManagement.title'),
        linkOptions: linkOptions({ to: '/supplier-groups/$groupId', params: { groupId } }),
    };

    if (role.isSuperAdmin()) {
        return [
            { label: translate('page.suppliers'), linkOptions: linkOptions({ to: '/supplier-groups' }) },
            groupManagement,
        ];
    }

    if (role.hasAdminRights()) {
        return [settings, groupManagement];
    }

    return [
        settings,
        {
            label: translate('settings.companyManagement.title'),
            linkOptions: linkOptions({ to: '/supplier-groups/$groupId/companies', params: { groupId } }),
        },
    ];
};

export const SupplierDetailPage = () => {
    const role = useRole();
    const { companyId, groupId } = useParams({ from: '/_app/supplier-groups/$groupId/companies/$companyId' });

    const supplierDetailQuery = useSupplierDetails(companyId);
    const editSupplierDetailsMutation = useSupplierDetailsEdit();

    useDocumentTitle(supplierDetailQuery.data?.name || translate('company.title'));

    const { tab } = useSearch({ from: '/_app/supplier-groups/$groupId/companies/$companyId' });
    const navigate = useNavigate({ from: '/supplier-groups/$groupId/companies/$companyId' });

    if (supplierDetailQuery.isPending) {
        return (
            <Page>
                <LoadingIndicator variant="page" />
            </Page>
        );
    }

    if (supplierDetailQuery.isError) {
        return (
            <Page>
                <AdvancedError />
            </Page>
        );
    }

    const supplier = supplierDetailQuery.data;

    const onSupplierDetailEditSubmit = async (data: SupplierCompanyMessage) => {
        await editSupplierDetailsMutation.mutateAsync({ id: companyId, supplierData: data });
    };

    const totals = {
        [CompanyTabs.DETAILS]: null,
        [CompanyTabs.SETTINGS]: null,
        [CompanyTabs.COMMUNICATION_PREFERENCES]: null,
    };

    return (
        <Page>
            <Box display="flex" flexDirection="column" gap={16}>
                <Box display="flex" flexDirection="column" gap={4}>
                    <Breadcrumbs links={getBreadcrumbLinks(groupId, role)} />

                    <Box display="flex" flexDirection="column">
                        <Text as="h1" variant="headline">
                            {translate('company.title')}
                        </Text>
                        <Text variant="body" color="foreground.subtle">
                            {supplier.name}
                        </Text>
                    </Box>
                </Box>

                <Box display="flex" flexDirection="column" gap={32}>
                    <Tabs
                        selectedContext={tab}
                        onSelect={(newTab: CompanyTabs) => navigate({ search: (prev) => ({ ...prev, tab: newTab }) })}
                        totals={totals}
                    />

                    <SupplierDetailsPageTabContent
                        tab={tab}
                        supplier={supplier}
                        onEditSubmit={onSupplierDetailEditSubmit}
                    />
                </Box>
            </Box>
        </Page>
    );
};
